import { useAuth0 } from '@auth0/auth0-react'
import {
  AppBar,
  BottomNavigationAction,
  Toolbar,
  useTheme,
  Box,
  Fab,
} from '@mui/material'
import {
  IconHome,
  IconLogin,
  IconUserCircle,
  IconCamera,
  IconSearch,
  IconUsers,
} from '@tabler/icons-react'
import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { Capacitor } from '@capacitor/core'
import { ROUTES } from '../../helpers/routes-helper'
import { useAuthHelper } from '../../hooks/useAuthHelper'
import { useDevice } from '../../hooks/useDevice'
import { useMenuScanFlow } from '../../hooks/useMenuScanFlow'
import { logEvent } from '../../analytics/analytics'
import {
  emitCameraPromptIfNeeded,
  emitCameraDeniedIfDenied,
} from '../../helpers/camera-analytics'
import MenuScanCoachingSheet, {
  useMenuScanCoaching,
} from '../MenuScanCoachingSheet'
import { errorLog } from '../../utils/log-helper'

interface NavigationItem {
  label: string
  icon: React.ReactElement
  route?: string
  login?: boolean
}

const BottomAppBar: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { isAuthenticated } = useAuth0()
  const { loginAuthUser } = useAuthHelper()
  const { isHybrid } = useDevice()
  const { startScan, uploading } = useMenuScanFlow()
  const [isScanning, setIsScanning] = useState(false)
  const scanning = isScanning || uploading
  const { requestCapture, coachingProps } = useMenuScanCoaching('nav_bar', 'nav')

  const handleNavigation = (item: NavigationItem): void => {
    if (item.login) {
      loginAuthUser()
    } else if (item.route) {
      // Track tab selection
      logEvent('tab_selected', {
        tab_name: item.label.toLowerCase(),
        screen_name: pathname,
        source: 'in_app',
      })
      // The Dine tab is a top-of-funnel entry into the anon group-session flow — attribute it
      // (nav_tab) so it's comparable with the Home CTA (home_cta) in the K-factor funnel.
      if (item.route === ROUTES.DINE) {
        logEvent('session_entry_clicked', {
          entry_point: 'nav_tab',
          is_logged_in: isAuthenticated,
          screen_name: pathname,
        })
      }
      navigate(item.route)
    }
  }

  const handleScanClick = (): void => {
    logEvent('menu_scan_opened', { screen_name: 'nav_bar', entry_point: 'nav' })
    // Show the pre-capture coaching cue first; the existing capture path runs on confirm.
    requestCapture(() => {
      void runCapture()
    })
  }

  const runCapture = async (): Promise<void> => {
    setIsScanning(true)

    try {
      // Check if we're on native platform (iOS/Android)
      if (isHybrid || Capacitor.isNativePlatform()) {
        // Request camera permissions and open camera directly
        // Capacitor Camera plugin will handle permission requests automatically
        await emitCameraPromptIfNeeded('nav_bar')
        logEvent('menu_scan_capture_shown', { screen_name: 'nav_bar' })
        const photo = await Camera.getPhoto({
          quality: 90,
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Camera, // Directly open camera, bypassing prompt
          allowEditing: false,
          promptLabelHeader: 'Scan a menu',
          promptLabelPhoto: 'Choose menu photo',
          promptLabelPicture: 'Take menu photo',
        })
        logEvent('camera_permission', { status: 'granted', screen_name: 'nav_bar' })

        if (!photo.dataUrl) {
          setIsScanning(false)
          return
        }

        // Convert dataUrl → Blob → File
        const res = await fetch(photo.dataUrl)
        const blob = await res.blob()
        const file = new File(
          [blob],
          `scan-${Date.now()}.jpg`,
          { type: blob.type || 'image/jpeg' },
        )

        try {
          await startScan(file, undefined, 'camera')
        } catch (e) {
          errorLog('[BottomAppBar] Scan upload failed', e)
        }
        setIsScanning(false)
      } else {
        // Web fallback: use file input
        // For web, we'll create a temporary file input and trigger it
        logEvent('menu_scan_capture_shown', { screen_name: 'nav_bar' })
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = 'image/*'
        input.capture = 'environment'
        input.style.display = 'none'

        input.onchange = async (event) => {
          const target = event.target as HTMLInputElement
          if (target.files && target.files[0]) {
            const file = target.files[0]
            try {
              await startScan(file, undefined, 'file_picker')
            } catch (e) {
              errorLog('[BottomAppBar] Scan upload failed', e)
            }
          }
          setIsScanning(false)
          document.body.removeChild(input)
        }

        input.oncancel = () => {
          setIsScanning(false)
          document.body.removeChild(input)
        }

        document.body.appendChild(input)
        input.click()
      }
    } catch (e) {
      // User cancelled or error. Only counts as a permission denial if the OS actually blocked
      // access (checked inside the helper) — a plain cancel is not a denial.
      void emitCameraDeniedIfDenied('nav_bar')
      errorLog('[BottomAppBar] Scan error', e)
      setIsScanning(false)
    }
  }

  // Two items each side of the center Scan FAB: Home | Search | [Scan] | Dine | Profile/Login.
  const leftItems: NavigationItem[] = [
    { label: 'Home', icon: <IconHome />, route: ROUTES.HOME },
    { label: 'Search', icon: <IconSearch />, route: ROUTES.SEARCH },
  ]
  const rightItems: NavigationItem[] = [
    { label: 'Planner', icon: <IconUsers />, route: ROUTES.DINE },
    isAuthenticated
      ? { label: 'Profile', icon: <IconUserCircle />, route: ROUTES.PROFILE }
      : { label: 'Login', icon: <IconLogin />, login: true },
  ]

  const isRouteActive = (route?: string): boolean => {
    return route ? `/${pathname.split('/')[1]}` === route : false
  }

  const renderNavAction = (item: NavigationItem): React.ReactElement => (
    <BottomNavigationAction
      key={item.label}
      label={item.label}
      icon={React.cloneElement(item.icon, { stroke: 2, size: 18 })}
      sx={{
        minWidth: 0,
        maxWidth: '87px',
        padding: '6px 0',
        '& .Mui-selected': {
          fontSize: '0.75rem !important',
        },
        '& .MuiBottomNavigationAction-label': {
          mt: '6px !important',
          fontSize: '0.7rem',
        },
        // Active tab carries the lone signal accent; inactive stays muted ink — restrained,
        // per the Intent-Intelligence language.
        color: isRouteActive(item.route)
          ? theme.palette.primary.main
          : theme.palette.text.secondary,
        backgroundColor: isRouteActive(item.route)
          ? theme.palette.primary[50]
          : 'transparent',
        borderRadius: isRouteActive(item.route) ? '8px' : 'none',
      }}
      onClick={() => handleNavigation(item)}
    />
  )

  // Routes where scan button should be hidden
  const hideScanRoutes = [
    '/onboard',
    '/subscription/success',
    '/subscription/canceled',
    '/unauthorized',
    '/recover-account',
    '/recover-blocked-account',
    '/404',
    '/restaurant/pending',
    '/restaurant/dashboard',
    '/restaurant/menus',
    '/restaurant/reviews',
    '/restaurant/recommendations',
    '/restaurant/notifications',
    '/restaurant/messages',
    '/restaurant/settings',
    '/restaurant/subscription',
    '/restaurant/influencer',
    '/restaurant/leaderboard',
  ]

  // Check if current path should hide scan button
  const shouldHideScan = hideScanRoutes.some((route) => pathname.startsWith(route))

  return (
    <>
    <AppBar
      position="relative"
      component="footer"
      sx={{
        flex:'0 0 auto',
        width: '100%',
        backgroundColor: theme.palette.background.paper,
        boxShadow: 'none',
      }}
    >
      <Toolbar
        sx={{
          zIndex: 1200,
          width: '100%',
          p: 1,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
          }}
        >
          {/* Left group - Home, Search */}
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              justifyContent: 'space-around',
              alignItems: 'center',
              pr: shouldHideScan ? 0 : '32px', // leave room for the center FAB
            }}
          >
            {leftItems.map(renderNavAction)}
          </Box>

          {/* Center - Scan FAB (context-aware) */}
          {!shouldHideScan && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                bottom: '12px',
              }}
            >
              <Fab
                color="primary"
                aria-label="Discover dishes + earn points"
                onClick={() => {
                  handleScanClick()
                }}
                disabled={scanning}
                sx={{
                  width: '64px',
                  height: '64px',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                  boxShadow: '0px 6px 16px -4px rgba(0, 0, 0, 0.28)',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    boxShadow: '0px 10px 20px -4px rgba(0, 0, 0, 0.34)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <IconCamera size={32} stroke={2} color="white" />
              </Fab>
            </Box>
          )}

          {/* Right group - Dine, Profile/Login */}
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              justifyContent: 'space-around',
              alignItems: 'center',
              pl: shouldHideScan ? 0 : '32px', // leave room for the center FAB
            }}
          >
            {rightItems.map(renderNavAction)}
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
    <MenuScanCoachingSheet {...coachingProps} />
    </>
  )
}

export default BottomAppBar;
